/* import __COLOCATED_TEMPLATE__ from './trending-topics-panel.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type { TrendingTopic } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topic-trends/trending-topics-data-resources';
import type Range from 'embercom/models/reporting/range';
import type { Topic } from 'embercom/components/reporting/automation/ai-insights/fin-insights/topics-data-resources';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type { TrendingTopicConversation } from 'embercom/components/reporting/automation/ai-insights/trending-topics-key-conversations';
import { convertMarkdownToHtml } from './optimize/summary-converter';
import moment from 'moment-timezone';
import RangeModel from 'embercom/models/reporting/range';
import { inject as service } from '@ember/service';
import type AppService from 'embercom/services/app-service';
import { cached } from 'tracked-toolbox';

interface Signature {
  Args: {
    showSidePanel: boolean;
    onClose: () => void;
    trendingTopic: TrendingTopic | undefined;
    selectedTopic: Topic | null;
    selectedSubTopic: string | null;
    conversationCount: number;
    isStandalone: boolean;
    trackAnalyticsEvent: (
      action: string,
      object: string,
      extraContext: Record<string, any>,
    ) => void;
  };
}

export default class TrendingTopicsPanel extends Component<Signature> {
  @service declare appService: AppService;

  @tracked showConversationsPanel = false;
  @tracked loadedConversationsForPanel: TrendingTopicConversation[] = [];
  @tracked initialSelectedConversationId: number | undefined = undefined;

  get formattedInsightExplanation(): string {
    return convertMarkdownToHtml(this.args.trendingTopic?.insightExplanation || '', true, true);
  }

  @cached
  get dateRange(): Range {
    // Calculate 12 complete weeks (Monday to Sunday) excluding the current incomplete week
    // Use the app's timezone consistently for calculation and storage
    let timezone = this.appService.app.timezone;
    let now = moment.tz(timezone);

    let lastCompleteWeekStart = now.clone().startOf('isoWeek').subtract(1, 'week');
    let lastCompleteWeekEnd = lastCompleteWeekStart.clone().endOf('isoWeek');

    let startDate = lastCompleteWeekStart.clone().subtract(11, 'weeks');

    // Pass ISO strings with timezone offset to preserve timezone info through the serialization chain
    return RangeModel.createFromParams(
      startDate.startOf('day').format(),
      lastCompleteWeekEnd.endOf('day').format(),
      timezone,
    );
  }

  @action
  onShowTrendingTopicsConversationPanel(conversations: TrendingTopicConversation[]) {
    if (conversations.length === 0) {
      return;
    }
    this.loadedConversationsForPanel = conversations;
    this.initialSelectedConversationId = conversations[0].id;
    this.showConversationsPanel = true;
  }

  @action
  onConversationSelected(conversationId: number, conversations: TrendingTopicConversation[]) {
    if (conversations.length === 0) {
      return;
    }
    this.loadedConversationsForPanel = conversations;
    this.initialSelectedConversationId = conversationId;
    this.showConversationsPanel = true;
  }

  @action
  onCloseTrendingTopicsConversationPanel() {
    this.showConversationsPanel = false;
    this.initialSelectedConversationId =
      this.loadedConversationsForPanel.length > 0
        ? this.loadedConversationsForPanel[0].id
        : undefined;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::TrendingTopicsPanel': typeof TrendingTopicsPanel;
  }
}
